import {Button, Modal, Spinner} from 'react-bootstrap'
import {useMutation} from '@apollo/client'
import {useEffect} from 'react'
import {toast} from 'react-toastify'
import {DELETE_RESOURCE_CENTER} from '../../../gql/mutations/resourceCenterMutations'
import {IResourceCenter, IDeleteResourceCenterResponse} from '../../../types/resourceCenter'
import {parseId} from '../../../helpers'
import {getResourceTypeIcon, getResourceTypeBadgeStyle, getResourceTypeLabel} from '../resourceTypeHelpers'
import moment from 'moment'
import { FormattedMessage, useIntl } from 'react-intl'
import { BACKEND_ERROR, RESOURCE_MESSAGES as R_, SPACE } from '../resourceCenterMessages'

interface IModalProps {
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  resource: IResourceCenter
}

export const ModalDeleteResource: React.FC<IModalProps> = ({
  isOpen,
  onClose,
  refetch,
  resource,
}) => {
  const intl = useIntl()
  const [deleteResource, {data, loading, error}] = useMutation<IDeleteResourceCenterResponse>(
    DELETE_RESOURCE_CENTER
  )

  useEffect(() => {
    if (error) {
      toast.error(intl.formatMessage(BACKEND_ERROR, {message: error.message}))
    }
  }, [error])

  useEffect(() => {
    if (!data) return

    if (data.deleteResourceCenter.success) {
      onClose()
      refetch()
      toast.success(data.deleteResourceCenter.message || intl.formatMessage(R_.toastResourceDeleted))
    } else if (
      data.deleteResourceCenter.errors &&
      data.deleteResourceCenter.errors.length > 0
    ) {
      toast.error(intl.formatMessage(BACKEND_ERROR, {message: data.deleteResourceCenter.errors[0].message}))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const handleDelete = async () => {
    try {
      await deleteResource({
        variables: {
          id: parseId(resource.id),
        },
      })
    } catch (err) {
      console.error('Error deleting resource:', err)
      toast.error(intl.formatMessage(R_.errDeleteResource))
    }
  }

  const fileName = resource.file?.split('/').pop()?.split('?')[0] || resource.url || intl.formatMessage(R_.unknownResource)
  const fileExtension = resource.file ? (fileName.split('.').pop()?.toUpperCase() || 'FILE') : 'URL'

  return (
    <Modal show={isOpen} onHide={onClose} centered backdrop="static">
      <Modal.Header closeButton className="bg-danger text-white">
        <Modal.Title className="d-flex align-items-center gap-2">
          <i className="bi bi-exclamation-triangle-fill" /> {intl.formatMessage(R_.deleteResource)}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="alert alert-warning mb-3">
          <i className="bi bi-exclamation-triangle me-2" />
          <FormattedMessage {...R_.deleteWarning} values={{b: (c) => <strong>{c}</strong>}} />
        </div>

        <p className="mb-3">{intl.formatMessage(R_.confirmDeleteResource)}</p>

        {/* Resource Info Card */}
        <div className="card bg-light">
          <div className="card-body">
            <div className="d-flex align-items-start gap-3 mb-3">
              <div
                style={{
                  width: 60,
                  height: 60,
                  fontSize: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#fff',
                  borderRadius: 8,
                  border: '2px solid #dee2e6',
                }}
              >
                {getResourceTypeIcon(resource.resourceType)}
              </div>
              <div className="flex-fill">
                <h6 className="mb-2 fw-bold">{resource.name}</h6>
                <div className="mb-2">
                  <span style={getResourceTypeBadgeStyle(resource.resourceType)}>
                    {getResourceTypeLabel(intl, resource.resourceType)}
                  </span>
                </div>
                {resource.description && (
                  <p className="text-muted small mb-2">{resource.description}</p>
                )}
              </div>
            </div>

            <hr />

            <div className="row small">
              <div className="col-6">
                <div className="mb-2">
                  <strong>{intl.formatMessage(R_.labelFile)}</strong>
                  <div className="text-muted">{fileName}</div>
                </div>
                <div className="mb-2">
                  <strong>{intl.formatMessage(R_.labelType)}</strong>
                  <div className="text-muted">{fileExtension}</div>
                </div>
              </div>
              <div className="col-6">
                <div className="mb-2">
                  <strong>{intl.formatMessage(R_.labelCreated)}</strong>
                  <div className="text-muted">
                    {moment(resource.created).format('MMM DD, YYYY')}
                  </div>
                </div>
                <div className="mb-2">
                  <strong>{intl.formatMessage(R_.labelViews)}</strong>
                  <div className="text-muted">
{intl.formatMessage(R_.viewsTimes, {count: resource.viewsCount})}
</div>
                </div>
              </div>
            </div>

            {resource.user && (
              <>
                <hr />
                <div className="small">
                  <strong>{intl.formatMessage(R_.labelCreatedBy)}</strong>{SPACE}
                  <span className="text-muted">
                    {resource.user.firstName} {resource.user.lastName}
                  </span>
                </div>
              </>
            )}

            {resource.documentTag && (
              <div className="small mt-2">
                <strong>{intl.formatMessage(R_.labelTag)}</strong>{SPACE}
                <span className="badge bg-secondary">{resource.documentTag.tagName}</span>
              </div>
            )}
          </div>
        </div>

        <div className="mt-3 p-3 border border-danger rounded bg-light">
          <div className="d-flex align-items-start gap-2">
            <i className="bi bi-info-circle text-danger mt-1" />
            <div className="small">
              <strong>{intl.formatMessage(R_.whatWillBeDeleted)}</strong>
              <ul className="mb-0 mt-1">
                <li>{intl.formatMessage(R_.bulletRecord)}</li>
                <li>
                  {intl.formatMessage(R_.bulletFile)} <code>{fileName}</code>
                </li>
                <li>{intl.formatMessage(R_.bulletMetadata)}</li>
              </ul>
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={loading}>
          {intl.formatMessage(R_.cancel)}
          </Button>
        <Button variant="danger" onClick={handleDelete} disabled={loading}>
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              {intl.formatMessage(R_.deleting)}
            </>
          ) : (
            <>
              <i className="bi bi-trash me-1" />
              {intl.formatMessage(R_.yesDeleteResource)}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
